// @ts-strict-ignore
import type { ValidationRejection } from "@altostra/type-validations";

import type { FetchRequestError } from "@lib/errors";
import { FetchInvalidPayloadShapeError } from "@lib/errors";
import type { RemoveFirstArgument } from "@lib/typeHelpers";

import type { AllRouteEndpoints } from "../routeTypes";
import type { InternalGetFetcherFunctionWithShadowError, GetFetcherFunctionWithShadowError } from "../types";
import type { CacheKeyParam, EndpointQueryKey, RootCacheKey } from "./types";
import { logToDataDog } from "./utils";

/**
 * Slow-tail bias threshold. When the feature is enabled, requests at or above
 * this duration always emit a Datadog action regardless of whether the session
 * was sampled. Preserves PQ-relevant outliers even when the sample rate is low.
 */
const SLOW_REQUEST_THRESHOLD_MS = 2000;

/**
 * Integer 0-100 rendered server-side from the `track_react_query_api_latency_pct`
 * feature flipper. Acts as both a master switch and a per-session sample rate:
 *   - 0  : feature fully off; nothing emits, including the slow tail (kill switch).
 *   - 1+ : feature on; the slow tail (>= SLOW_REQUEST_THRESHOLD_MS) always emits,
 *          and N% of sessions additionally emit on every request.
 * Defaults to 0 (off) when the global is unset, e.g. local dev where the Datadog
 * scripts are not loaded.
 */
const SAMPLE_RATE: number = (window as any).RUM_API_LATENCY_SAMPLE_RATE ?? 0;
const FEATURE_ENABLED = SAMPLE_RATE > 0;
const SAMPLED_SESSION = Math.random() * 100 < SAMPLE_RATE;

export const shouldEmitToDataDog = (
  loadDuration: number,
  sampledSession: boolean = SAMPLED_SESSION,
  featureEnabled: boolean = FEATURE_ENABLED
): boolean => featureEnabled && (sampledSession || loadDuration >= SLOW_REQUEST_THRESHOLD_MS);

/**
 * Adapter from react-query fetcher function to a more natural fetcher function.
 *
 * This takes the fetcher function and passes in the query key, minus the root cache key
 * to the function. It also passes the abort signal as an optional last parameter
 */
export const reactQueryFetcherAdapter = <
  CacheKey extends AllRouteEndpoints,
  QueryKey extends readonly [rootKey: RootCacheKey<CacheKey>, ...originalKey: CacheKeyParam[]],
  Args extends RemoveFirstArgument<QueryKey>,
  Result,
  Error extends FetchRequestError = FetchRequestError,
>(
  cacheKey: CacheKey,
  fetcherFunction: InternalGetFetcherFunctionWithShadowError<Error, CacheKey, QueryKey, Args, Result>,
  validator?: (value: unknown, rejectionReasons?: (rejection: ValidationRejection) => void) => boolean
): GetFetcherFunctionWithShadowError<Error, CacheKey, QueryKey, Args, Result> => {
  return (
    { queryKey: [, ...rest], signal, meta }: EndpointQueryKey<CacheKey, Args>,
    customizedFetcherFunction?: Function,
    directCallFetcher = false,
    args?: Args
  ) => {
    // If directCallFetcher is true, we are calling the fetcher directly with passed in args
    if (directCallFetcher) {
      return fetcherFunction(signal, ...args);
    }

    // If customizedFetcher is provided, we use that instead of the default fetcherFunction
    const activeFetcherFunction = customizedFetcherFunction || fetcherFunction;

    const startTime = Date.now();

    return activeFetcherFunction(signal, ...(rest as Args)).then(
      (result) => {
        FetchInvalidPayloadShapeError.validateShape({
          cacheKey,
          result,
          validator,
          logException: meta?.logException as ((error: Error) => void) | undefined,
        });

        const loadDuration = Date.now() - startTime;
        if (shouldEmitToDataDog(loadDuration)) {
          logToDataDog({
            actionName: "react-query-request-resolve",
            url: cacheKey as string,
            loadDuration,
            // `estimated` indicates whether the *response data* is sampled — a
            // Pusher-job concept that has no meaning for React Query HTTP fetches.
            // Passed explicitly to override logToDataDog's `true` default.
            estimated: false,
          });
        }

        return result;
      },
      (error) => {
        const loadDuration = Date.now() - startTime;
        if (shouldEmitToDataDog(loadDuration)) {
          logToDataDog({
            actionName: "react-query-request-error",
            url: cacheKey as string,
            loadDuration,
            errorText: error?.message ?? String(error),
            estimated: false,
          });
        }
        throw error;
      }
    );
  };
};
